import React from 'react';
import { LiveSessionDecisionTreeRunFieldsFragment } from '../../../../../../../generated/core-api/graphql-react-query';
import useSessionContext from '../../../../../../LiveContext/SessionContextProvider/useSessionContext';
import { DetectionGroup } from '../../../../Contexts/DetectionGroupContext';

export type LiveFeedItem =
  | { kind: 'detection', key: string, timestamp: number, detectionGroup: DetectionGroup }
  | { kind: 'run', key: string, timestamp: number, run: LiveSessionDecisionTreeRunFieldsFragment };

// Merges detections and decision-tree runs into one chronological feed
// without touching DetectionGroupContext itself (kept detection-only -- too
// many consumers rely on its current shape, e.g. the panel tab's unseen
// count). Takes detectionGroups as a parameter rather than reading
// useDetectionGroups() directly, since the panel and the Session Live page
// each source their groups differently (live visibility-filtered vs. full
// history) but both need the same run-merging. Sub-runs (a run started from
// within another run) surface inside their parent's card, not as their own
// feed entry.
export default function useLiveFeedItems(detectionGroups: DetectionGroup[]): LiveFeedItem[] {
  const { session } = useSessionContext();
  const runs = session?.decisionTreeRuns ?? [];

  return React.useMemo(() => {
    const detectionItems: LiveFeedItem[] = detectionGroups.map((detectionGroup) => ({
      kind: 'detection', key: `detection-${detectionGroup.id}`, timestamp: detectionGroup.lastOccurrence, detectionGroup,
    }));
    const runItems: LiveFeedItem[] = runs
      .filter((run) => !run.parentId)
      .map((run) => ({
        kind: 'run', key: `run-${run.id}`, timestamp: run.finishedAt ?? run.createdAt, run,
      }));
    return [...detectionItems, ...runItems].sort((a, b) => b.timestamp - a.timestamp);
  }, [detectionGroups, runs]);
}
